// src/components/bitacora/Bitacora.jsx
// Bitácora general del sistema (solo administrador): todos los movimientos, global o por sucursal.
// Periodos rápidos (Hoy, Ayer, Esta semana, Este mes, Personalizado) en hora de Guatemala;
// los filtros se aplican al cambiar, sin botón "Filtrar".
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiGet } from '../../services/api';
import { fechaCorta, hoyGT } from '../finanzas/estados';
import { menosDias } from '../cierres/PeriodoFiltro';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import '../finanzas/ui.css';
import '../usuarios/Usuarios.css';
import './Bitacora.css';
import FondoModal from '../common/FondoModal';

const PERIODOS = [['hoy', 'Hoy'], ['ayer', 'Ayer'], ['semana', 'Esta semana'], ['mes', 'Este mes'], ['personalizado', 'Personalizado']];
const RESULTADOS = ['Exitoso', 'Fallido', 'Rechazado', 'Error']; // respaldo si /opciones no responde
const FILTROS_VACIOS = { sucursalId: '', usuario: '', modulo: '', accion: '', resultado: '' };
const CLASE_RESULTADO = { Exitoso: 'on', Fallido: 'off', Rechazado: 'warn', Error: 'off' };

/** Rango (YYYY-MM-DD, ambos inclusive) de un periodo rápido. La semana empieza el lunes. */
export function rangoPeriodo(periodo, hoy, manual) {
  if (periodo === 'hoy') return { desde: hoy, hasta: hoy };
  if (periodo === 'ayer') { const d = menosDias(hoy, 1); return { desde: d, hasta: d }; }
  if (periodo === 'semana') {
    const [y, m, d] = hoy.split('-').map(Number);
    const diasDesdeLunes = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
    return { desde: menosDias(hoy, diasDesdeLunes), hasta: hoy };
  }
  if (periodo === 'mes') return { desde: `${hoy.slice(0, 8)}01`, hasta: hoy };
  return manual.desde <= manual.hasta ? manual : { desde: manual.hasta, hasta: manual.desde };
}

const fechaHora = (v) => new Date(v).toLocaleString('es-GT', { timeZone: 'America/Guatemala', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const rolTxt = (r) => (r === 'admin' ? 'Administrador' : r === 'viewer' ? 'Cajero' : '');
const etiquetaUsuario = (u) => `${u.nombre}${u.rol ? ` · ${rolTxt(u.rol)}` : ''}${u.deshabilitado ? ' (deshabilitado)' : ''}`;
const contador = (n) => `${n.toLocaleString('es-GT')} ${n === 1 ? 'movimiento' : 'movimientos'}`;

function Valores({ titulo, datos }) {
  if (!datos || !Object.keys(datos).length) return null;
  return <div className="bitacora-valores"><h3>{titulo}</h3>
    <dl>{Object.entries(datos).map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v === null || v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd></React.Fragment>)}</dl>
  </div>;
}

export default function Bitacora() {
  // Con una sucursal actual, la Bitácora solo muestra esa sucursal (la API también lo impone).
  const { sucursalId: sucursalActual, todas, nombreSucursal } = useSucursalActiva();
  const hoy = hoyGT();
  const [periodo, setPeriodo] = useState('hoy');
  const [manual, setManual] = useState({ desde: hoy, hasta: hoy });
  const [filtros, setFiltros] = useState(FILTROS_VACIOS);
  const [texto, setTexto] = useState('');
  const [textoAplicado, setTextoAplicado] = useState('');
  const [pagina, setPagina] = useState(1);
  const [datos, setDatos] = useState({ items: [], total: 0, porPagina: 50, diasRetencion: 30 });
  // Catálogo de los filtros: todo lo que el sistema puede registrar, no solo lo que ya ocurrió.
  const [opciones, setOpciones] = useState({ modulos: [], acciones: [], usuarios: [], sucursales: [], resultados: RESULTADOS });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [detalle, setDetalle] = useState(null);
  const ultimaPeticion = useRef(0);

  const rango = useMemo(() => rangoPeriodo(periodo, hoy, manual), [periodo, hoy, manual]);

  useEffect(() => { apiGet('/bitacora/opciones').then(setOpciones).catch(() => {}); }, [sucursalActual]);
  // Al cambiar la sucursal actual, los filtros que dependían de ella dejan de tener sentido.
  useEffect(() => { setFiltros((f) => ({ ...f, sucursalId: '', usuario: '' })); setPagina(1); }, [sucursalActual]);

  // La búsqueda por descripción se aplica sola al dejar de escribir.
  useEffect(() => {
    const t = setTimeout(() => { setTextoAplicado(texto.trim()); setPagina(1); }, 400);
    return () => clearTimeout(t);
  }, [texto]);

  const cargar = useCallback(async () => {
    const id = ++ultimaPeticion.current; // descarta respuestas de filtros anteriores
    setCargando(true); setError('');
    try {
      const qs = new URLSearchParams(Object.entries({ ...filtros, desde: rango.desde, hasta: rango.hasta, texto: textoAplicado, pagina })
        .filter(([, v]) => v !== '' && v != null));
      const r = await apiGet(`/bitacora?${qs}`);
      if (id === ultimaPeticion.current) setDatos(r);
    } catch (e) {
      if (id === ultimaPeticion.current) setError(e.message || 'No se pudo cargar la bitácora');
    } finally {
      if (id === ultimaPeticion.current) setCargando(false);
    }
  }, [filtros, rango, textoAplicado, pagina, sucursalActual]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [cargar]);

  const elegirPeriodo = (id) => {
    if (id === 'personalizado' && periodo !== 'personalizado') setManual(rango); // parte del periodo que se estaba viendo
    setPeriodo(id); setPagina(1);
  };
  const cambiarFecha = (k, v) => { if (v) { setManual((m) => ({ ...m, [k]: v })); setPagina(1); } };
  const campo = (k, v) => { setFiltros((f) => ({ ...f, [k]: v, ...(k === 'modulo' ? { accion: '' } : {}) })); setPagina(1); };
  const limpiar = () => { setFiltros(FILTROS_VACIOS); setTexto(''); setTextoAplicado(''); setPeriodo('hoy'); setPagina(1); };
  const hayFiltros = periodo !== 'hoy' || texto || Object.values(filtros).some(Boolean);

  // Con un módulo elegido, solo sus acciones (en el orden del catálogo); con Todos, todas sin repetir.
  const acciones = useMemo(() => {
    const lista = [...new Set(opciones.acciones.filter((a) => !filtros.modulo || a.modulo === filtros.modulo).map((a) => a.accion))];
    return filtros.modulo ? lista : lista.sort((a, b) => a.localeCompare(b, 'es'));
  }, [opciones.acciones, filtros.modulo]);
  const paginas = Math.max(1, Math.ceil(datos.total / datos.porPagina));
  const etiquetaRango = rango.desde === rango.hasta ? fechaCorta(rango.desde) : `${fechaCorta(rango.desde)} – ${fechaCorta(rango.hasta)}`;

  return <main className="usuarios-shell bitacora">
    <header className="bitacora-header">
      <div><h1>Bitácora</h1>
        <p className="bitacora-sub">{todas ? 'Movimientos de todo el sistema' : `Movimientos de ${nombreSucursal}`} · se conservan {datos.diasRetencion || 30} días</p></div>
      <button type="button" className="fx-btn" onClick={cargar} disabled={cargando}>Actualizar</button>
    </header>

    <section className="usuarios-card bitacora-card">
      {/* 1. Periodo */}
      <div className="bitacora-periodo">
        <div className="bitacora-chips" role="group" aria-label="Periodo">
          {PERIODOS.map(([id, label]) => <button key={id} type="button" aria-pressed={periodo === id}
            className={`fx-btn ${periodo === id ? 'fx-btn--primary' : ''}`} onClick={() => elegirPeriodo(id)}>{label}</button>)}
        </div>
        {periodo === 'personalizado' && <div className="bitacora-rango">
          <label>Desde<input type="date" value={manual.desde} max={hoy} onChange={(e) => cambiarFecha('desde', e.target.value)} /></label>
          <label>Hasta<input type="date" value={manual.hasta} max={hoy} onChange={(e) => cambiarFecha('hasta', e.target.value)} /></label>
        </div>}
      </div>

      {/* 2. Filtros */}
      <div className="bitacora-filtros">
        {todas && <label>Sucursal<select value={filtros.sucursalId} onChange={(e) => campo('sucursalId', e.target.value)}>
          <option value="">Todas</option>
          {opciones.sucursales.map((s) => <option key={s.id} value={s.id}>{s.nombre}{s.activo === false ? ' (inactiva)' : ''}</option>)}</select></label>}
        <label>Usuario<select value={filtros.usuario} onChange={(e) => campo('usuario', e.target.value)}>
          <option value="">Todos</option>{opciones.usuarios.map((u) => <option key={u.uid} value={u.uid}>{etiquetaUsuario(u)}</option>)}</select></label>
        <label>Módulo<select value={filtros.modulo} onChange={(e) => campo('modulo', e.target.value)}>
          <option value="">Todos</option>{opciones.modulos.map((m) => <option key={m} value={m}>{m}</option>)}</select></label>
        <label>Acción<select value={filtros.accion} onChange={(e) => campo('accion', e.target.value)}>
          <option value="">Todas</option>{acciones.map((a) => <option key={a} value={a}>{a}</option>)}</select></label>
        <label>Resultado<select value={filtros.resultado} onChange={(e) => campo('resultado', e.target.value)}>
          <option value="">Todos</option>{(opciones.resultados?.length ? opciones.resultados : RESULTADOS).map((r) => <option key={r} value={r}>{r}</option>)}</select></label>
      </div>

      {/* 3. Búsqueda */}
      <div className="bitacora-busqueda">
        <input type="search" aria-label="Buscar por descripción" placeholder="Buscar por descripción…" value={texto} onChange={(e) => setTexto(e.target.value)} />
        <button type="button" className="fx-btn" onClick={limpiar} disabled={!hayFiltros}>Limpiar filtros</button>
      </div>

      <div className="bitacora-resumen" aria-live="polite">
        <strong>{cargando ? 'Cargando…' : contador(datos.total)}</strong><span>{etiquetaRango}</span>
      </div>
      {error && <p role="alert" className="bitacora-error">{error}</p>}

      <div className="usuarios-table-wrap"><table className="usuarios-table bitacora-tabla">
        <thead><tr><th>Fecha/hora</th><th>Sucursal</th><th>Usuario</th><th>Módulo</th><th>Acción</th><th>Descripción</th><th>Resultado</th></tr></thead>
        <tbody>
          {!cargando && !error && !datos.items.length && <tr><td colSpan="7" className="bitacora-vacio">No hay movimientos para este período.</td></tr>}
          {datos.items.map((r) => <tr key={r.id} className="bitacora-fila" tabIndex={0} onClick={() => setDetalle(r)}
            onKeyDown={(e) => { if (e.key === 'Enter') setDetalle(r); }}>
            <td data-label="Fecha/hora">{fechaHora(r.fechaHora)}</td>
            <td data-label="Sucursal">{r.sucursalNombre || (r.sucursalId ? r.sucursalId : 'Todas')}</td>
            <td data-label="Usuario">{r.usuarioNombre || '—'}</td>
            <td data-label="Módulo">{r.modulo}</td>
            <td data-label="Acción">{r.accion}</td>
            <td data-label="Descripción" className="bitacora-desc">{r.descripcion || '—'}</td>
            <td data-label="Resultado"><span className={`usuarios-badge ${CLASE_RESULTADO[r.resultado] || ''}`}>{r.resultado}</span></td>
          </tr>)}
        </tbody></table></div>
      {paginas > 1 && <nav className="bitacora-paginas" aria-label="Paginación">
        <button type="button" className="fx-btn" disabled={pagina <= 1 || cargando} onClick={() => setPagina((p) => p - 1)}>Anterior</button>
        <span>Página {pagina} de {paginas}</span>
        <button type="button" className="fx-btn" disabled={pagina >= paginas || cargando} onClick={() => setPagina((p) => p + 1)}>Siguiente</button>
      </nav>}
    </section>

    {detalle && <FondoModal className="usuarios-backdrop" onClose={() => setDetalle(null)}>
      <section className="usuarios-modal" role="dialog" aria-modal="true" aria-label="Detalle del movimiento" onMouseDown={(e) => e.stopPropagation()}>
        <header><h2>{detalle.modulo} · {detalle.accion}</h2><button type="button" onClick={() => setDetalle(null)} aria-label="Cerrar">×</button></header>
        <div className="usuarios-detail">
          <p><b>Fecha/hora:</b> {fechaHora(detalle.fechaHora)}</p>
          <p><b>Usuario:</b> {detalle.usuarioNombre || '—'}{detalle.rol ? ` (${rolTxt(detalle.rol)})` : ''}</p>
          <p><b>Sucursal:</b> {detalle.sucursalNombre || 'Todas'}</p>
          <p><b>Registro:</b> {detalle.entidad || '—'}{detalle.entidadId ? ` · ${detalle.entidadId}` : ''}</p>
          <p><b>Descripción:</b> {detalle.descripcion || '—'}</p>
          <p><b>Resultado:</b> {detalle.resultado}</p>
          <Valores titulo="Datos anteriores" datos={detalle.datosAnteriores} />
          <Valores titulo="Datos nuevos" datos={detalle.datosNuevos} />
        </div>
      </section></FondoModal>}
  </main>;
}
